/**
 * The Julian calendar and the dates countries switched to the Gregorian one.
 *
 * The date arithmetic follows Reingold and Dershowitz, *Calendrical Calculations: The
 * Ultimate Edition* (Cambridge University Press, 2018): a Julian year is a leap year when it
 * is divisible by 4, the epoch is R.D. −1 (1 January 1 CE Julian is 30 December 1 BCE in
 * the proleptic Gregorian calendar), and a date and a fixed day number convert into each
 * other with the formulas below. Only years 1 CE and later are supported, so there is no
 * year-zero question.
 *
 * "Proleptic" means the calendar is run backwards or forwards past the dates when anyone
 * used it. Historians quote events in the calendar the place was using then, which is why
 * the adoption table matters. Pope Gregory XIII's bull *Inter gravissimas* (24 February
 * 1582) set the reform; each country then chose its own switch day.
 */

import { mod, rdFromGregorian } from "@/lib/tool-kit/calendars/rd";
import {
  type CivilDate,
  civilFromDays,
} from "@/lib/tool-kit/converters/week-number";

/** R.D. of 1 January 1 CE in the Julian calendar. */
export const JULIAN_EPOCH = -1;

export function isJulianLeapYear(year: number): boolean {
  return mod(year, 4) === 0;
}

export function julianMonthLength(year: number, month: number): number {
  if (month === 2) {
    return isJulianLeapYear(year) ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

export function isValidJulianDate(date: CivilDate): boolean {
  return (
    Number.isInteger(date.year) &&
    date.year >= 1 &&
    date.month >= 1 &&
    date.month <= 12 &&
    date.day >= 1 &&
    date.day <= julianMonthLength(date.year, date.month)
  );
}

/** R.D. of a Julian date (year ≥ 1). */
export function fixedFromJulian(date: CivilDate): number {
  const { year, month, day } = date;
  let correction = -2;
  if (month <= 2) {
    correction = 0;
  } else if (isJulianLeapYear(year)) {
    correction = -1;
  }
  return (
    JULIAN_EPOCH -
    1 +
    365 * (year - 1) +
    Math.floor((year - 1) / 4) +
    Math.floor((367 * month - 362) / 12) +
    correction +
    day
  );
}

/** The Julian date of an R.D. (R.D. ≥ −1). */
export function julianFromFixed(rd: number): CivilDate {
  const year = Math.floor((4 * (rd - JULIAN_EPOCH) + 1464) / 1461);
  const priorDays = rd - fixedFromJulian({ year, month: 1, day: 1 });
  let correction = 2;
  if (rd < fixedFromJulian({ year, month: 3, day: 1 })) {
    correction = 0;
  } else if (isJulianLeapYear(year)) {
    correction = 1;
  }
  const month = Math.floor((12 * (priorDays + correction) + 373) / 367);
  const day = rd - fixedFromJulian({ year, month, day: 1 }) + 1;
  return { year, month, day };
}

/**
 * How many days the Gregorian date label runs ahead of the Julian one: the Gregorian date's
 * own label, read as a Julian date, lands this many days after the true day. It is 10 in
 * 1582, 11 in 1752, 13 from 1 March 1900 to 28 February 2100.
 */
export function gregorianJulianGap(rd: number): number {
  const gregorian = civilFromDays(rd - 719_163);
  return fixedFromJulian(gregorian) - rd;
}

/** `1731/32`: the double year used for 1 January to 24 March while the year began at Lady Day. */
export function doubleYear(julianYear: number): string {
  const previous = julianYear - 1;
  return `${previous}/${String(julianYear % 100).padStart(2, "0")}`;
}

/** Julian dates from 1 January to 24 March fell in the "old" year while the year began on 25 March. */
export function isBeforeLadyDay(date: CivilDate): boolean {
  return date.month < 3 || (date.month === 3 && date.day <= 24);
}

export interface AdoptionRegion {
  id: string;
  /** Last day kept in the Julian calendar. */
  lastJulian: CivilDate;
  /** First day kept in the Gregorian calendar. */
  firstGregorian: CivilDate;
  /** The civil year began on 25 March (Lady Day) until the Gregorian switch. */
  ladyDayYear?: boolean;
}

/**
 * Switch dates as the standard references give them (Reingold and Dershowitz; the British
 * Calendar (New Style) Act 1750, which ordered 2 September 1752 to be followed by 14
 * September; the papal bull of 1582) and as a tabulation of adoption dates lists them.
 */
export const ADOPTION_REGIONS: readonly AdoptionRegion[] = [
  {
    id: "papalSpain",
    lastJulian: { year: 1582, month: 10, day: 4 },
    firstGregorian: { year: 1582, month: 10, day: 15 },
  },
  {
    id: "france",
    lastJulian: { year: 1582, month: 12, day: 9 },
    firstGregorian: { year: 1582, month: 12, day: 20 },
  },
  {
    id: "denmark",
    lastJulian: { year: 1700, month: 2, day: 18 },
    firstGregorian: { year: 1700, month: 3, day: 1 },
  },
  {
    id: "britain",
    lastJulian: { year: 1752, month: 9, day: 2 },
    firstGregorian: { year: 1752, month: 9, day: 14 },
    ladyDayYear: true,
  },
  {
    id: "sweden",
    lastJulian: { year: 1753, month: 2, day: 17 },
    firstGregorian: { year: 1753, month: 3, day: 1 },
  },
  {
    id: "bulgaria",
    lastJulian: { year: 1916, month: 3, day: 31 },
    firstGregorian: { year: 1916, month: 4, day: 14 },
  },
  {
    id: "russia",
    lastJulian: { year: 1918, month: 1, day: 31 },
    firstGregorian: { year: 1918, month: 2, day: 14 },
  },
  {
    id: "romania",
    lastJulian: { year: 1919, month: 3, day: 31 },
    firstGregorian: { year: 1919, month: 4, day: 14 },
  },
  {
    id: "greece",
    lastJulian: { year: 1923, month: 2, day: 15 },
    firstGregorian: { year: 1923, month: 3, day: 1 },
  },
];

/** R.D. of the last Julian day. */
export function lastJulianRd(region: AdoptionRegion): number {
  return fixedFromJulian(region.lastJulian);
}

/** R.D. of the first Gregorian day (always the day after the last Julian one). */
export function firstGregorianRd(region: AdoptionRegion): number {
  return rdFromGregorian(
    region.firstGregorian.year,
    region.firstGregorian.month,
    region.firstGregorian.day
  );
}

/** Calendar days whose names were dropped at the switch (the label jump, less one). */
export function daysSkipped(region: AdoptionRegion): number {
  const { lastJulian, firstGregorian } = region;
  return fixedFromJulian(firstGregorian) - fixedFromJulian(lastJulian) - 1;
}

/**
 * Which calendar a region was using on a day: the Julian one up to its switch, the
 * Gregorian one from then on.
 */
export function calendarInUse(
  region: AdoptionRegion,
  rd: number
): "julian" | "gregorian" {
  return rd <= lastJulianRd(region) ? "julian" : "gregorian";
}
